import React from "react";
import { DefaultButton, NormalPeoplePicker, PrimaryButton, Stack, Text, IPersonaProps, IBasePickerSuggestionsProps, Label, TagPicker, ITag } from "@fluentui/react";
import { FieldArray, Formik } from "formik";
import { addMergedCustomer, updateMergedCustomer } from "../../../services/merged-customers/MergedCustomerService";
import { ICustomers, IMergedCustomers } from "../../../vm";
import { childrenGap } from "../../common/Constants";
import CustomDrawer from "../../common/CustomDrawer";
import { CustomTextField } from "../../common/Library";
import { ToastContext } from "../../common/ToastProvider";
import * as yup from "yup";
import { Loading } from "../../../services/Loading";
import { getAllCustomers, getCustomers, getDatacentersForACustomer } from "../../../services/customers/CustomerService";

const mergedCustomersSchema = yup.object().shape({
    name: yup.string().required("required"),
    mergedCustomers: yup.array().of(
        yup.object().shape({
            customerId: yup.string().required("required"),
            datacenterIds: yup.array().of(yup.string().required("required")).min(1, "Select at least one datacenter")
        })
    ),
});

export interface ManageMergedCustomerDialogProps {
  data?: IMergedCustomers;
  handleDialogClose: any;
}

const ManageMergedCustomerDialog: React.FC<ManageMergedCustomerDialogProps> = ({
  data,
  handleDialogClose
}) => {
  //state
  const [isLoading, setLoading] = React.useState(false as boolean);
  const [mergedCustomer, setMergedCustomer] = React.useState({
      name: "",
      mergedCustomers: [
        {
          customerId: "",
          datacenterIds: []
        },
        {
          customerId: "",
          datacenterIds: []
      }
    ],
      ...data
  } as IMergedCustomers);
  const [tempCustomers, setTempCustomers] = React.useState([] as ICustomers[]);
//   const [selectedCustomers, setSelectedCustomers] = React.useState([] as IPersonaProps[]);
  //   const [tempDatacenters, setTempDatacenters] = React.useState([] as IDataCenter[]);
  const [dcDictByCustomer, setDcDictByCustomer] = React.useState({} as {[ key: string]: ITag[]});

  // custom hooks
  const { showToast } = React.useContext(ToastContext);

  // hooks
  React.useEffect(() => {
    const fetchData = async () => {
        if (data && data.mergedCustomers.length > 0) { 
            await getCustomersAndDatacenters(data.mergedCustomers.map((d) => d.customerId))
        }
    }
    fetchData();
  }, []);

  const getCustomersAndDatacenters = async (customerIds: string[]) => {
        setLoading(true);
        const result = await getCustomers(customerIds);
        if (result?.success) {
            const tempDict: any = {};
            for (let i = 0; i < result.data.length; i++) {
                const customer = result.data[i];
                const customerId = customer._id as string;
                const datacentersResp = await getDatacentersForACustomer({ customerId });
                if (datacentersResp?.success) {
                    tempDict[customerId] = datacentersResp.data.map((d) => { return { key: d._id, name: d.name }});
                } else {
                   showToast(datacentersResp?.message || "Error while getting datacenters", "error");
                } 
            }
            setTempCustomers(result.data);
            setDcDictByCustomer(tempDict);
        } else {
            showToast(result?.message || "Error while getting customers", "error");
        }
        setLoading(false);
  }

  const closeAddDialog = (data?: IMergedCustomers) => {
    handleDialogClose(data);
  };

  const pickerSuggestionsProps: IBasePickerSuggestionsProps = {
    // suggestionsHeaderText: "Suggested Card holders",
    noResultsFoundText: "No customer found",
  };

  const onFilterChanged = async (
    filterText: string,
    selectedCustomer: string[],
    tagList?: IPersonaProps[]
  ): Promise<IPersonaProps[]> => {
    if (filterText && filterText.length >= 3) {
      let result = await getAllCustomers({
        search: filterText,
        offset: 0,
        limit: 12,
      });
      if (result?.success) {
        const customers: IPersonaProps[] = [];

        result?.data.forEach((item: any) => {
            if(!selectedCustomer.includes(item._id)) {
                customers.push({
                    text: item.name,
                    secondaryText: item.regSapNo,
                    id: item._id,
                });
            }
        });
        setTempCustomers([ ...tempCustomers, ...result.data ]);
        return customers;
      } else {
        return [];
      }
    }
    return [];
  };

  const pickerSuggestionsPropsForDc: IBasePickerSuggestionsProps = {
    noResultsFoundText: "No datacenter found",
  };

  const filterSuggestedTags = (filterText: string, tagList: ITag[], customerId: string): ITag[] => {
    let allDataCenterList = customerId ? [...dcDictByCustomer[customerId]] : [];

    return filterText
      ? allDataCenterList.filter(
          (tag) =>
            tag.name.toLowerCase().indexOf(filterText.toLowerCase()) === 0 &&
            !listContainsTagList(tag, tagList)
        )
      : [];
  };

  const showAllTags = (customerId: string, selectedItems?: ITag[] | undefined) => {
    let allDataCenterList = customerId ? [...dcDictByCustomer[customerId]] : [];
    return removeDuplicates(allDataCenterList, selectedItems);
  };

  function removeDuplicates(tagList: ITag[], tags?: ITag[]) {
    return tagList.filter((tag) => !listContainsTagList(tag, tags));
  }

  const listContainsTagList = (tag: ITag, tagList?: ITag[]) => {
    if (!tagList || !tagList.length || tagList.length === 0) {
      return false;
    }
    return tagList.some((compareTag) => compareTag.key === tag.key);
  };

  const getDcForCustomer = async (customerId: string) => {
    const result = await getDatacentersForACustomer({ customerId });
    if (result?.success) {
        let tempDict = { ...dcDictByCustomer };
        tempDict[customerId] = result.data.map((d) => { return { key: d._id, name: d.name }});
        setDcDictByCustomer(tempDict);
    } else {
        showToast(result?.message || "Error while getting customer's datacenters", "error");
    }
  };

  const getSelectedCustomer = (customerId: string) : IPersonaProps[] => {
      let found = tempCustomers.find((d) => d._id === customerId);
      if (found) {
        return [{ id: found._id as string, text: found.name + ` (${found.regSapNo})` }];
      } else {
        return [];
      }
  }

  const getFormikErrors = (errors: any, touched: any, index: number, isDc: boolean, shouldReturnText: boolean) => {
      if (isDc) {
          if (shouldReturnText) return errors && errors.mergedCustomers && errors.mergedCustomers[index] && errors.mergedCustomers[index].datacenterIds;
          let isError = false;
          if (errors && errors.mergedCustomers && errors.mergedCustomers[index] && errors.mergedCustomers[index].datacenterIds && touched && touched.mergedCustomers && touched.mergedCustomers[index] && touched.mergedCustomers[index].datacenterIds) {
             isError = true;
          }
          return isError;
      } else {
         if (shouldReturnText) return errors && errors.mergedCustomers && errors.mergedCustomers[index] && errors.mergedCustomers[index].customerId;
          let isError = false;
          if (errors && errors.mergedCustomers && errors.mergedCustomers[index] && errors.mergedCustomers[index].customerId && touched && touched.mergedCustomers && touched.mergedCustomers[index] && touched.mergedCustomers[index].customerId) {
             isError = true;
          }
          return isError; 
      }
  }

  return (
    <React.Fragment>
      {isLoading && <Loading />}
      <CustomDrawer
        title={
          mergedCustomer._id
            ? "Update Merged Customer"
            : "Add Merged Customer"
        }
        handelOpen={true}
        onClose={() => closeAddDialog()}
      >
        <Stack>
          <Formik
            initialValues={mergedCustomer}
            validationSchema={mergedCustomersSchema}
            onSubmit={async (
              values: IMergedCustomers,
              { setSubmitting }
            ) => {
              let obj = { ...values };
              let result;
              setLoading(true);
              if (mergedCustomer._id) {
                result = await updateMergedCustomer(obj);
              } else {
                result = await addMergedCustomer(obj);
              }
              setLoading(false);
              if (result.success) {
                  const customerName: any = [];
                  const dcName: any = {};
                  result.data.mergedCustomers.map((d) => {
                      const customer = tempCustomers.find((dd) => dd._id === d.customerId);
                      if (customer) {
                        let cName = customer.name + ` (${customer.regSapNo})`;
                        customerName.push(cName);
                        d.customerName = cName;
                      }
                      const datacenter: string[] = [];
                      d.datacenterIds.map((ddd) => {
                          const dc = dcDictByCustomer[d.customerId].find((dddd) => dddd.key === ddd);
                          if (dc) {
                              dcName[dc.name] = dc.name;
                              datacenter.push(dc.name);
                          }
                      })
                      d.datacenterName = datacenter.join(", ");
                  })
                closeAddDialog({ ...result.data, datacenterNames: Object.keys(dcName).join(", "), customerNames: customerName.join(", ")});
              } else {
                showToast(
                  result?.message ||
                    `Error while ${
                      mergedCustomer._id ? "updating" : "adding"
                    } customer`,
                  "error"
                );
              }
              setSubmitting(false);
            }}
          >
            {({ handleSubmit, values, setFieldValue, errors, touched }) => (
              <form onSubmit={handleSubmit}>
                <Stack wrap tokens={childrenGap}>
                  <Stack.Item>
                    <CustomTextField name={"name"} label={"Name"} />
                  </Stack.Item>
                  <Stack.Item>
                  <FieldArray name="mergedCustomers">
                    {({ insert, remove }) => (
                      <Stack tokens={childrenGap}>
                          <Stack.Item>
                              <Stack horizontal horizontalAlign="space-between" verticalAlign="center">
                                  <Stack.Item>
                                      <Text className="text-bold">Combinations</Text>
                                  </Stack.Item>
                                  <Stack.Item>
                                    <DefaultButton
                                      text="Add A Combination"
                                      onClick={() => insert(values.mergedCustomers.length, {
                                        customerId: "",
                                        datacenterIds: []
                                      })}
                                    />
                                  </Stack.Item>
                              </Stack>
                          </Stack.Item>
                          <Stack.Item>
                              {values.mergedCustomers.map((d, index) => (
                                <fieldset>
                                  <legend>{`Combination ${index + 1}`}</legend>
                                  <Stack wrap tokens={childrenGap} className={`${values.mergedCustomers.length > 2 ? "" : "pt-8"}`}>
                                  {values.mergedCustomers.length > 2 && (<Stack.Item align="end">
                                  <DefaultButton
                                    text="Remove"
                                    onClick={() => remove(index)}
                                    className="borderless-btn"
                                  />
                                  </Stack.Item>)}
                                  <Stack.Item>
                                      <Stack wrap tokens={childrenGap}>
                                         <Stack.Item className="custom-picker" grow={1}>
                                            <Label>Customers</Label>
                                            <NormalPeoplePicker
                                                inputProps={{
                                                placeholder: "Search customers by name/sap code",
                                                }}
                                                itemLimit={1}
                                                onResolveSuggestions={(filter) => onFilterChanged(filter, values.mergedCustomers.map((d) => d.customerId))}
                                                selectedItems={getSelectedCustomer(d.customerId)}
                                                removeButtonAriaLabel="Remove"
                                                pickerSuggestionsProps={pickerSuggestionsProps}
                                                onChange={async (items: any) => {
                                                    let customerId = "";
                                                    if (items.length > 0) {
                                                        customerId = items[0].id;
                                                    }
                                                    setFieldValue(`mergedCustomers[${index}].customerId`, customerId);
                                                    // setSelectedCustomers([items]);
                                                    if (customerId) {
                                                      await getDcForCustomer(customerId);
                                                    } else {
                                                      setFieldValue(`mergedCustomers[${index}].datacenterIds`, []);
                                                    }
                                                }}
                                            />
                                            {getFormikErrors(errors, touched, index, false, false) && (
                                                <Text className="error-msg">
                                                  {getFormikErrors(errors, touched, index, false, true)}
                                                </Text>
                                            )}
                                         </Stack.Item>
                                         <Stack.Item className="custom-picker" grow={1}>
                                            <Label>Datacenters</Label>
                                            <TagPicker
                                                removeButtonAriaLabel="Remove"
                                                selectedItems={d.datacenterIds.map((dd) => {
                                                    const found = dcDictByCustomer[d.customerId]?.find((ele) => ele.key === dd);
                                                        if (found) {
                                                        return { key: found.key as string, name: found.name };
                                                        } else {
                                                        return { key : "", name: "" };
                                                        }
                                                })}
                                                onResolveSuggestions={(filter, selectedItems) => filterSuggestedTags(filter, selectedItems as ITag[], d.customerId)}
                                                pickerSuggestionsProps={pickerSuggestionsPropsForDc}
                                                onEmptyResolveSuggestions={(selectedItems) => showAllTags(d.customerId, selectedItems)}
                                                inputProps={{
                                                    placeholder: "Search datacenters",
                                                }}
                                                onChange={(items: any) => {
                                                    let datacenterIds: string[] = [];
                                                    items.forEach((i: any) => {
                                                    datacenterIds.push(i.key);
                                                    });
                                                    setFieldValue(`mergedCustomers[${index}].datacenterIds`, datacenterIds);
                                                }}
                                                />
                                                {getFormikErrors(errors, touched, index, true, false) && (
                                                    <Text className="error-msg">
                                                      {getFormikErrors(errors, touched, index, true, true)}
                                                    </Text>
                                                )}
                                         </Stack.Item>
                                      </Stack>
                                  </Stack.Item>
                               </Stack>
                               </fieldset>
                              ))}
                          </Stack.Item>
                      </Stack>
                    )}
                  </FieldArray>   
                  </Stack.Item>
                  <Stack.Item>
                    <Stack
                      horizontal
                      wrap
                      tokens={childrenGap}
                      horizontalAlign="end"
                      className="pt-16"
                    >
                      <DefaultButton
                        text="Cancel"
                        onClick={() => closeAddDialog()}
                        className="borderless-btn"
                      />
                      <PrimaryButton
                        type="submit"
                        text={mergedCustomer._id ? "Update" : "Add"}
                      />
                    </Stack>
                  </Stack.Item>
                </Stack>
              </form>
            )}
          </Formik>
        </Stack>
      </CustomDrawer>
    </React.Fragment>
  );
};

export default ManageMergedCustomerDialog;
